import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Auth } from "aws-amplify";
import {
  Button,
  FormControl,
  Spinner,
  FormGroup,
  Toast,
} from "react-bootstrap";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

const validAuthStates = ["resetPassword"];
const validationSchema = Yup.object().shape({
  password: Yup.string()
    .min(8, "*Password must have at least 8 characters")
    .max(50, "*Password can't be longer than 50 characters")
    .required("*Password is required"),
  code: Yup.string()
    .min(6, "*Code must have at least 6 characters")
    .required("*Code is required"),
});

const CustomResetPassword = ({ authState, onStateChange, account }) => {
  const [showToast, setShowToast] = useState(false);
  const [resetPasswordStatus, setResetPasswordStatus] = useState({
    isProcessing: false,
    errorMsg: "",
  });

  useEffect(() => {
    // Reset status of reset-reset form
    setResetPasswordStatus({ isProcessing: false, errorMsg: "" });
  }, [authState]);

  const toggleToast = () => {
    setShowToast(!showToast);
  };

  const resetPasswordSubmit = async (values) => {
    const { code, password } = values;
    try {
      setResetPasswordStatus({
        isProcessing: true,
        errorMsg: "",
      });

      await Auth.forgotPasswordSubmit(account.email, code, password);

      setResetPasswordStatus({
        isProcessing: false,
        errorMsg: "",
      });

      // Go to sign-in form after reset password success
      onStateChange("signIn");
      toggleToast();
    } catch (e) {
      setResetPasswordStatus({
        isProcessing: false,
        errorMsg:
          e.message || "Fail to update your password. Please try again!",
      });
    }
  };

  const changeToSigninForm = () => {
    onStateChange("signIn");
  };

  return (
    <>
      <Toast show={showToast} onClose={toggleToast} autohide>
        <Toast.Body>Your password was updated successfully.</Toast.Body>
      </Toast>
      {validAuthStates.includes(authState) ? (
        <Formik
          initialValues={{
            email: "",
          }}
          validationSchema={validationSchema}
          onSubmit={resetPasswordSubmit}
        >
          {({ errors, touched }) => (
            <Form className="auth-form" autoComplete="off">
              <FormGroup controlId="account">
                <FormControl
                  type="text"
                  value={account.email}
                  disabled
                  className="auth-input"
                />
              </FormGroup>
              <Field name="code">
                {({ field }) => (
                  <FormGroup controlId="code">
                    <FormControl
                      type="text"
                      placeholder="Verification Code"
                      value={field.value}
                      onChange={field.onChange}
                      className="auth-input"
                      isValid={touched.code && !errors.code}
                      isInvalid={touched.code && errors.code}
                      autoComplete="new-code"
                      data-testid="code-reset-password"
                    />
                    <FormControl.Feedback type="invalid">
                      {errors.code}
                    </FormControl.Feedback>
                  </FormGroup>
                )}
              </Field>
              <Field name="password">
                {({ field }) => (
                  <FormGroup controlId="password">
                    <FormControl
                      type="password"
                      placeholder="New password"
                      value={field.value}
                      onChange={field.onChange}
                      data-prop={"password"}
                      className="auth-input"
                      isValid={touched.password && !errors.password}
                      isInvalid={touched.password && errors.password}
                      autoComplete="new-password"
                      data-testid="password-reset"
                    />
                    <FormControl.Feedback type="invalid">
                      {errors.password}
                    </FormControl.Feedback>
                  </FormGroup>
                )}
              </Field>

              <div>
                <Button
                  type="submit"
                  disabled={resetPasswordStatus.isProcessing}
                >
                  Reset my password
                  {resetPasswordStatus.isProcessing && (
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="isProcessing"
                      className="ml-1"
                      aria-hidden="true"
                    />
                  )}
                </Button>
                {resetPasswordStatus.errorMsg && (
                  <p className={"mt-2 mb-0 error-message"}>
                    {resetPasswordStatus.errorMsg}
                  </p>
                )}

                <Button
                  variant="link"
                  className="auth-navigate"
                  onClick={changeToSigninForm}
                  data-testid="login-account"
                >
                  <em>Login to your account</em>
                </Button>

                {/* <p>
                  {`Don't have an account?`}{" "}
                  <Button
                    variant="link"
                    onClick={() => onStateChange("signUp")}
                  >
                    <strong>Sign up</strong>
                  </Button>
                </p>*/}
              </div>
            </Form>
          )}
        </Formik>
      ) : null}
    </>
  );
};

CustomResetPassword.propTypes = {
  authState: PropTypes.string,
  onStateChange: PropTypes.func,
  account: PropTypes.object.isRequired,
};

export default CustomResetPassword;
